import React, { forwardRef } from 'react'
import { cn } from '@/utils/cn'

const inputBaseClasses = [
  'w-full px-4 py-3 rounded-xl border text-gray-900 placeholder-gray-400',
  'text-[15px] leading-normal',
  'transition-[border-color,box-shadow,background-color] duration-200 ease-out',
  'focus:outline-none focus:ring-1 focus:ring-offset-0 focus:ring-portal-orange/40 focus:border-portal-orange',
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50/80',
].join(' ')

const inputDefaultClasses = [
  'bg-gray-50/80 border-gray-200',
  'hover:border-gray-300 hover:bg-gray-50',
  'focus:bg-white',
].join(' ')

const inputErrorClasses = [
  'border-red-400 bg-red-50/30',
  'focus:ring-red-400/40 focus:border-red-400',
  'hover:border-red-400',
].join(' ')

export interface FormInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'className'
> {
  label?: string
  helperText?: string
  error?: string
  leftAddon?: React.ReactNode
  rightAddon?: React.ReactNode
  wrapperClassName?: string
  labelClassName?: string
  inputClassName?: string
  helperClassName?: string
}

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      helperText,
      error,
      leftAddon,
      rightAddon,
      wrapperClassName,
      labelClassName,
      inputClassName,
      helperClassName,
      id,
      required,
      disabled,
      ...inputProps
    },
    ref
  ) => {
    const inputId = id ?? inputProps.name
    const showHelper = error ?? helperText

    return (
      <div className={cn('space-y-2', wrapperClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'block text-sm font-medium text-gray-700 tracking-tight',
              error && 'text-red-600',
              disabled && 'opacity-60',
              labelClassName
            )}
          >
            {label}
            {required && (
              <span className="text-red-500 ml-0.5" aria-hidden>
                *
              </span>
            )}
          </label>
        )}

        <div className="relative">
          {leftAddon && (
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none flex items-center justify-center">
              {leftAddon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            required={required}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={showHelper ? `${inputId}-helper` : undefined}
            className={cn(
              inputBaseClasses,
              error ? inputErrorClasses : inputDefaultClasses,
              leftAddon && 'pl-11',
              rightAddon && 'pr-11',
              inputClassName
            )}
            {...inputProps}
          />
          {rightAddon && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none flex items-center justify-center">
              {rightAddon}
            </div>
          )}
        </div>

        {showHelper && (
          <p
            id={inputId ? `${inputId}-helper` : undefined}
            className={cn(
              'text-xs mt-1.5 leading-relaxed',
              error ? 'text-red-600 font-medium' : 'text-gray-500',
              helperClassName
            )}
          >
            {error ?? helperText}
          </p>
        )}
      </div>
    )
  }
)

FormInput.displayName = 'FormInput'
